import { format } from 'date-fns';
import { CheckCircle2, ChevronLeft, ChevronRight, Circle, MapPin, Phone } from 'lucide-react';
import { useMemo, useState } from 'react';

import { MakeCallModal } from '@/components/common/calls/make-call-modal';
import { cn } from '@/lib/utils';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import type { MiniTortClaimDetailSchema, PrerequisiteStatus } from '@/services/api/generated/miniTortService';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';
import { CoverageCategory, type CoverageSchema, IdentifierType } from '@/services/types/coverage-types';

import { PREREQUISITE_READY } from './constants';
import type { IncidentLocationSchema } from '@/services/types/incident-types';
import type { MatterClient } from '@/services/types/matter-client-types';

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '';
  try { return format(new Date(d), 'EEE, MMM d, yyyy'); } catch { return d; }
};

const Label = ({ children }: { children: string }) => (
  <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-2">{children}</span>
);

const STATUS_LABELS: Record<PrerequisiteStatus, string> = {
  pending: 'Pending', extracted: 'Extracted', processed: 'Processed',
  complete: 'Complete', approved: 'Approved', failed: 'Failed', manual: 'Manual', waived: 'Waived',
};

const StatusBadge = ({ status }: { status: PrerequisiteStatus | undefined }) => {
  if (!status) return null;
  const color = PREREQUISITE_READY.has(status) ? 'bg-emerald-100 text-emerald-700' : status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-500';
  return <span className={cn('ml-1.5 px-1 py-0.5 rounded text-[9px] font-medium', color)}>{STATUS_LABELS[status]}</span>;
};

const ClickablePhone = ({ phone, label = 'Phone', matterId }: {
  phone: string | null | undefined; label?: string; matterId: string;
}) => {
  const [open, setOpen] = useState(false);
  if (!phone) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={`Click to call ${label}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded">
        <Phone className="h-3 w-3" /><span>{phone}</span>
      </button>
      <MakeCallModal isOpen={open} onClose={() => setOpen(false)} initialPhoneNumber={phone}
        initialContactLabel={label} matterId={matterId} source="insurance_card" />
    </>
  );
};

const getPolicyNum = (c: CoverageSchema) =>
  c.policy_identifiers?.find((p) => p.identifier_type === IdentifierType.POLICY_NUMBER)?.identifier_value ?? null;

const SV_KEYS = ['streetview_image_url_0', 'streetview_image_url_90', 'streetview_image_url_180', 'streetview_image_url_270'] as const;
const SV_LABELS = ['Street View N', 'Street View E', 'Street View S', 'Street View W'] as const;
const NAV_BTN = 'absolute top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none';

const ImageCarousel = ({ location }: { location: IncidentLocationSchema }) => {
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const images = useMemo(() => {
    const imgs: { url: string; label: string }[] = [];
    if (location.satellite_image_url) imgs.push({ url: location.satellite_image_url, label: 'Satellite' });
    SV_KEYS.forEach((k, i) => { const u = location[k]; if (u) imgs.push({ url: u, label: SV_LABELS[i] }); });
    return imgs;
  }, [location]);
  if (!images.length) return null;
  const toggle = () => setExpanded((p) => !p);
  return (
    <div className="relative w-full overflow-hidden group cursor-pointer transition-[height] duration-300 ease-in-out"
      style={{ height: expanded ? '20rem' : '8rem' }} onClick={toggle} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
      aria-label={expanded ? 'Collapse image' : 'Expand image'}>
      <img src={images[idx].url} alt={images[idx].label}
        className={cn('h-full w-full object-cover transition-opacity duration-300', expanded ? 'opacity-90' : 'opacity-50 group-hover:opacity-75')} />
      <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-0.5 rounded text-[10px] font-medium">{images[idx].label}</div>
      {images.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
            className={cn(NAV_BTN, 'left-1.5')} aria-label="Previous image"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
            className={cn(NAV_BTN, 'right-1.5')} aria-label="Next image"><ChevronRight className="h-4 w-4" /></button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((img, i) => (
              <button key={img.label} type="button" onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={cn('h-1.5 w-1.5 rounded-full transition-colors', i === idx ? 'bg-white' : 'bg-white/50')}
                aria-label={`View ${img.label}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CoverageCol = ({ label, coverage, status }: {
  label: string; coverage: CoverageSchema | undefined; status: PrerequisiteStatus | undefined;
}) => (
  <div>
    <div className="flex items-center"><Label>{label}</Label><StatusBadge status={status} /></div>
    {coverage ? (
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-zinc-900 truncate">{coverage.carrier?.name || 'Unknown'}</p>
        {coverage.named_insured?.length ? <p className="text-xs font-medium text-zinc-600 truncate">{coverage.named_insured.join(', ')}</p> : null}
        {getPolicyNum(coverage) && <p className="text-xs font-medium text-zinc-500">{getPolicyNum(coverage)}</p>}
      </div>
    ) : <p className="text-xs font-medium text-zinc-500">None</p>}
  </div>
);

export const MiniTortDetailsCard = ({ matterId, claim, client, className }: {
  matterId: string; claim: MiniTortClaimDetailSchema; client?: MatterClient; className?: string;
}) => {
  const { data: incidents } = useGetIncidentsQuery({ matterId }, { skip: !matterId });
  const incident = incidents?.[0];
  const { data: coverages } = useGetCoveragesQuery({ matterId });
  const cov1p = coverages?.find((c) => c.category === CoverageCategory.FIRST_PARTY || c.category === CoverageCategory.FIRST_PARTY_HEALTH);
  const cov3p = coverages?.find((c) => c.category === CoverageCategory.THIRD_PARTY);
  const { data: policeReports } = usePoliceReportsListQuery({ matterId }, { skip: !matterId });
  const pr = policeReports?.[0];
  const loc = incident?.normalized_location;
  const hasImages = loc?.satellite_image_url || SV_KEYS.some((k) => loc?.[k]);
  const name = client?.name || (claim.submission ? `${claim.submission.claimant_first_name} ${claim.submission.claimant_last_name}` : null);
  const email = client?.email || claim.submission?.claimant_email;
  const phone = client?.phone_numbers?.[0]?.phone_number || claim.submission?.claimant_phone;
  const prNum = pr?.report_identifier ?? claim.submission?.report_number;
  const prDept = pr?.police_department?.name ?? claim.submission?.police_department;

  return (
    <div className={cn('rounded-lg border border-zinc-200 bg-white overflow-hidden', className)}>
      <div className="px-6 py-5">
        <div className="flex justify-between items-start gap-6">
          {name && (
            <div className="min-w-0">
              <Label>Claimant</Label>
              <p className="text-sm font-semibold text-zinc-900">{name}</p>
              {(email || phone || client?.date_of_birth) && (
                <div className="flex items-center gap-4 mt-2">
                  {email && <a href={`mailto:${email}`} className="text-xs font-medium text-primary hover:text-primary/80 transition-colors truncate">{email}</a>}
                  {phone && <ClickablePhone phone={phone} label={name} matterId={matterId} />}
                  {client?.date_of_birth && <span className="text-xs font-medium text-zinc-500 shrink-0">DOB {fmtDate(client.date_of_birth)}</span>}
                </div>
              )}
            </div>
          )}
          {incident && (
            <div className="text-right shrink-0">
              <Label>Accident</Label>
              {incident.date_of_loss && (
                <p className="text-sm font-semibold text-zinc-900">
                  {fmtDate(incident.date_of_loss)}
                  {incident.time_of_loss && <span className="text-zinc-500 ml-1.5 text-xs font-medium">{incident.time_of_loss}</span>}
                </p>
              )}
              {incident.incident_type && <p className="text-xs font-medium text-zinc-500 mt-1">{incident.incident_type}</p>}
            </div>
          )}
        </div>
      </div>
      {hasImages && loc && <ImageCarousel location={loc} />}
      {(incident?.location || loc?.formatted_address || incident?.weather_conditions) && (
        <div className="px-6 py-3 flex items-center gap-2 text-xs font-medium text-zinc-600">
          {(loc?.formatted_address || incident?.location) && (
            <>
              <MapPin className="h-3 w-3 text-zinc-400 shrink-0" />
              <span className="truncate">{loc?.formatted_address || incident?.location}</span>
              {loc?.google_maps_uri && <a href={loc.google_maps_uri} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline shrink-0">Maps</a>}
            </>
          )}
          {incident?.weather_conditions && (
            <span className="text-zinc-500 shrink-0">{(loc?.formatted_address || incident?.location) ? '·' : ''} {incident.weather_conditions}</span>
          )}
        </div>
      )}
      {incident?.description && (
        <div className="mx-6 mb-5 rounded-md bg-zinc-50 px-4 py-3">
          <p className="text-xs font-medium text-zinc-600 whitespace-pre-wrap leading-relaxed">{incident.description}</p>
        </div>
      )}
      {(cov1p || cov3p || prNum || prDept) && (
        <div className="px-6 py-5 border-t border-zinc-100">
          <div className="grid grid-cols-3 gap-0 divide-x divide-zinc-200 [&>*]:px-4 [&>*:first-child]:pl-0 [&>*:last-child]:pr-0">
            <div><CoverageCol label="1P Coverage (Client)" coverage={cov1p} status={claim.coverage_1p_status} /></div>
            <div><CoverageCol label="3P Coverage (Defendant)" coverage={cov3p} status={claim.coverage_3p_status} /></div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label>Police Report</Label>
                <StatusBadge status={claim.police_report_status} />
                {pr?.attestation_status === 'verified_received' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Circle className="h-3.5 w-3.5 text-zinc-300" />}
              </div>
              {prNum && <p className="text-xs font-semibold text-zinc-900 mt-2">#{prNum}</p>}
              {prDept && <p className="text-xs font-medium text-zinc-600 truncate">{prDept}</p>}
              {!prNum && !prDept && <p className="text-xs font-medium text-zinc-500 mt-2">None</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
